import { inject } from '@angular/core';

import { type CanvasElementId } from '@blaze-runtime/engine-domain';
import { NavMenu } from '@blaze-shared/elements-domain';
import {
  type IconSelectOption,
  type LinkAction,
  type PropertiesConfig,
  type PropertiesConfigItemDefinition,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { FALLBACK_THEME } from '@blaze-shared/theming-domain';

import { CollectionListBusService } from '../../services/collection-list-bus.service';
import {
  validateFontSizeWithLineHeight,
  validateLineHeightWithFontSize,
} from '../../utils/elements/validation.utils';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { type AvatarMenuAction, type AvatarPropsModel, type MenuState } from './avatar.models';

export function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('avatar', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'content.data.source',
        'Source',
        [],
        [
          epc.utils.disabledField(
            epc.fields.selectFieldFromList(
              'content.data.source',
              'Source',
              [{ value: 'Users', label: 'Users' }],
              { hideLabel: true },
            ),
            () => 'Cannot be changed',
          ),

          epc.fields.selectFieldFromList(
            'content.fields.avatarType',
            'Avatar type',
            [
              { label: 'Image', value: 'image' },
              { label: 'Initials', value: 'initials' },
            ],
            { allowNone: false },
          ),
          epc.utils.conditionalField(
            epc.fields.selectFieldFromUsersColumns('content.fields.image', '', 'Image'),
            'content.fields.avatarType',
            (avatarType) => avatarType === 'initials',
          ),
          epc.utils.conditionalField(
            epc.fields.selectFieldFromUsersColumns('content.fields.initials', '', 'String'),
            'content.fields.avatarType',
            (avatarType) => avatarType === 'image',
          ),
          epc.fields.selectFieldFromUsersColumns('content.fields.name', 'Name'),

          epc.utils.separatedField(
            epc.fields.selectFieldFromList(
              'content.fields.avatarTypeFallback',
              'Fallback',
              [
                { label: 'Image', value: 'image' },
                { label: 'Initials', value: 'initials' },
                { label: 'Placeholder', value: 'placeholder' },
              ],
              { allowNone: false },
            ),
          ),
          epc.utils.conditionalField(
            epc.fields.selectFieldFromUsersColumns('content.fields.imageFallback', '', 'Image'),
            'content.fields.avatarTypeFallback',
            (avatarTypeFallback) => avatarTypeFallback !== 'image',
          ),
          epc.utils.conditionalField(
            epc.fields.selectFieldFromUsersColumns('content.fields.initialsFallback', '', 'String'),
            'content.fields.avatarTypeFallback',
            (avatarTypeFallback) => avatarTypeFallback !== 'initials',
          ),
        ],
        {
          enabledByDefault: true,
          subsectionOptions: {
            hideLabel: false,
          },
        },
      ),

      epc.subsections.subsection(
        'content.menu',
        'Menu',
        [menuItemsField('content.menu.items', 'Menu')],
        {
          optional: true,
          itemAdd: (model: { root: { elementId: CanvasElementId } }) => {
            inject(CollectionListBusService).postItemAdd({
              elementId: model.root.elementId,
              path: 'content.menu.items',
            });
            return undefined;
          },
        },
      ),

      epc.subsections.visibilitySubsection(),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),

        epc.subsections.subsection('style.general', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),

        epc.subsections.subsection('style.label', 'Label', [
          epc.fields.colorField('style.label.color', 'Color', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),

        epc.subsections.subsection(
          'style.font',
          'Font',
          [
            ...epc.fields.fontFamilyAndWeightFields(
              'style.font.fontFamily',
              'style.font.fontWeight',
            ),
            epc.fields.fontStyleField('style.font.style', 'Style', ['line-through', 'bold']),
            epc.utils.observedField(
              epc.fields.sliderField('style.font.fontSize', 'Size', [
                { min: 8, max: 72, unit: 'px' },
              ]),
              (props: AvatarPropsModel, value: ValueWithUnitOfMeasure) => {
                return validateLineHeightWithFontSize(props, value, 'style.font.lineHeight');
              },
            ),
            epc.utils.observedField(
              epc.fields.sliderField('style.font.lineHeight', 'Line height', [
                { min: 8, max: 72, unit: 'px' },
              ]),
              (props: AvatarPropsModel, value: ValueWithUnitOfMeasure) => {
                return validateFontSizeWithLineHeight(props, value, 'style.font.fontSize');
              },
            ),
          ],
          {
            hideWhen: (_elementId: string, _resolver: any, props: AvatarPropsModel) =>
              !props?.content?.fields?.name,
          },
        ),

        epc.subsections.subsection(
          'style.menu',
          'Menu',
          [
            epc.fields.radiosField(
              'style.menu.menuStateView',
              'Menu',
              [
                <IconSelectOption<MenuState>>{ value: 'default', label: 'Default' },
                <IconSelectOption<MenuState>>{ value: 'hover', label: 'Hover' },
              ],
              { hideLabel: true },
            ),
            ...menuStateFields('default'),
            ...menuStateFields('hover'),

            epc.utils.separator(),

            epc.fields.colorField('style.menu.background', 'Menu background', {
              allowGradient: false,
              allowNone: false,
            }),
            epc.fields.colorField('style.menu.outline', 'Menu outline', {
              allowGradient: false,
              allowNone: true,
            }),
          ],
          {
            hideWhen: (_elementId: string, _resolver: any, props: AvatarPropsModel) =>
              !props?.content?.menu?.enabled,
          },
        ),

        epc.subsections.subsection('style.layout', 'Layout', [
          epc.fields.sliderField('style.layout.imageSize', 'Avatar size', [
            { min: 8, max: 100, unit: 'px' },
          ]),
          ...epc.utils.conditionalFields(
            [
              epc.fields.positionField(
                'style.layout.imagePosition',
                'Avatar position',
                [
                  { value: 'left', label: 'Left', icon: 'icon_aligns_to_left_16' },
                  { value: 'right', label: 'Right', icon: 'icon_aligns_to_right_16' },
                ],
                { defaultValue: 'left' },
                { renderType: 'icon' },
              ),
              epc.fields.alignField('style.layout.labelAlign', 'Align', {
                vertical: false,
                excludedOptions: ['center'],
              }),
            ],
            'content.fields.name',
            (nameField: string) => !nameField,
          ),
        ]),

        epc.subsections.subsection(
          'style.initials',
          'Initials',
          [
            epc.fields.colorField('style.initials.background', 'Background', {
              allowGradient: false,
              allowNone: false,
              defaultValue: { color1: FALLBACK_THEME.value.fill, selectorType: 'solid' },
            }),

            epc.fields.colorField('style.initials.textColor', 'Initials', {
              allowGradient: false,
              allowNone: false,
            }),

            ...epc.fields.fontFamilyAndWeightFields(
              'style.initials.font.fontFamily',
              'style.initials.font.fontWeight',
            ),
            epc.fields.fontStyleField('style.initials.font.style', 'Style', [
              'line-through',
              'bold',
            ]),
            epc.utils.observedField(
              epc.fields.sliderField('style.initials.font.fontSize', 'Size', [
                { min: 8, max: 72, unit: 'px' },
              ]),
              (props: AvatarPropsModel, value: ValueWithUnitOfMeasure) => {
                return validateLineHeightWithFontSize(
                  props,
                  value,
                  'style.initials.font.lineHeight',
                );
              },
            ),
            epc.utils.observedField(
              epc.fields.sliderField('style.initials.font.lineHeight', 'Line height', [
                { min: 8, max: 72, unit: 'px' },
              ]),
              (props: AvatarPropsModel, value: ValueWithUnitOfMeasure) => {
                return validateFontSizeWithLineHeight(props, value, 'style.initials.font.fontSize');
              },
            ),
          ],
          {
            hideWhen: (_elementId: string, _resolver: any, props: AvatarPropsModel) => {
              return (
                props.content.fields.avatarType !== 'initials' &&
                props.content.fields.avatarTypeFallback !== 'initials'
              );
            },
          },
        ),
      ],
      true,
    ),
  ]);
}

function menuItemsField(
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'collectionList'> {
  return {
    path,
    inputType: 'collectionList',
    label,
    inputOptions: {
      fieldAction: 'AVATAR_EDIT_ACTION',
      fieldPaneTitle: 'Menu Action',
      fieldConfig: (index) => [
        epc.subsections.subsection(
          `${path}.${index}`,
          '',
          [
            epc.fields.textInput(`${path}.${index}.label`, 'Label'),
            epc.fields.iconField(`${path}.${index}.icon`, 'Icon'),
            epc.fields.selectFieldFromList(
              `${path}.${index}.clickAction.action`,
              'On click',
              NavMenu.MENU_ACTIONS,
              { allowNone: true, showSelection: true },
            ),
            epc.utils.conditionalField(
              epc.fields.selectFieldFromApi(
                `${path}.${index}.clickAction.navigatePageId`,
                'Open page',
                'LIST_PAGES',
                { allowNone: false, showSelection: true, placeholder: 'Select page' },
              ),
              `${path}.${index}.clickAction.action`,
              (action: LinkAction) => action !== 'GoToPage',
            ),
            epc.utils.conditionalField(
              epc.fields.textInput(`${path}.${index}.clickAction.navigateUrl`, 'URL'),
              `${path}.${index}.clickAction.action`,
              (action: LinkAction) => action !== 'Url',
            ),
          ],
          { hideLabel: true },
        ),
      ],
      iconField: 'icon',
      fieldFactory: ({ id }): AvatarMenuAction => ({
        id,
        label: `Action ${id + 1}`,
        clickAction: {
          action: undefined,
        },
      }),
      hideLabel: true,
      thickItems: true,
      hoverItems: true,
    },
  };
}

function menuStateFields(state: MenuState): PropertiesConfigItemDefinition[] {
  return epc.utils.conditionalFields(
    [
      epc.fields.colorField(`style.menu.${state}.label`, 'Label', {
        allowGradient: false,
        allowNone: false,
      }),
      epc.fields.colorField(`style.menu.${state}.background`, 'Background'),
      epc.fields.colorField(`style.menu.${state}.outline`, 'Outline', {
        allowGradient: false,
        allowNone: true,
      }),
    ],
    'style.menu.menuStateView',
    (menuState: MenuState) => menuState !== state,
  );
}
